import { useRef, type ReactNode } from "react";

import { cx } from "@/lib/cx";
import { Button } from "./Button";
import "./motion/pixel-motion.css";

/**
 * One-of-N Material segmented control: a field-tonal track with a selected
 * accent-tonal pill inset in it.
 *
 * Hand-rolled three times before this existed — the stats range picker, and the
 * card editor's lane and priority rows — and each copy marked the selected
 * option with a tint measuring 1.15:1 against its own container, crossfaded
 * over 150ms. Two consequences, both caught in screenshots: at rest you could
 * not tell which option was chosen, and mid-switch *two* options looked chosen
 * while the fade ran (`UI-REVIEW.md` P6).
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS ARE FIXED BY CONSTRUCTION AND NONE OF THEM IS A PROP
 * ---------------------------------------------------------------------------
 *
 * 1. **The selected pill is accent-tonal with accent ink**, never an outline
 *    or a white knob. It matches every selected/filter container in the app.
 *
 * 2. **The pill slides with a transform.** Labels switch immediately, while
 *    the single selection marker travels in 200ms. Text stays readable on the
 *    track and on the pill in both sky themes. Reduced motion is instant.
 *
 * 3. **Every segment is the same width.** iOS segments are equal-width, and it
 *    is also what lets the pill be positioned arithmetically instead of
 *    measured — no layout effect, no first-frame flash at the wrong offset, and
 *    it is correct on the very first render.
 *
 * A hairline divider sits between two *unselected* neighbours and disappears
 * next to the pill, which is the detail that makes the control read as one
 * object rather than as a row of buttons.
 *
 * Keyboard model is the ARIA one: the group is a single tab stop, arrows move
 * between options, and moving the cursor also selects — which is right for a
 * radiogroup and is what a range picker or a lane picker actually wants.
 */

export type SegmentedOption<T extends string | number> = {
  value: T;
  /** Usually text; may be a glyph, in which case pass `ariaLabel` too. */
  label: ReactNode;
  /** Required when `label` is not readable text. */
  ariaLabel?: string;
  title?: string;
  disabled?: boolean;
};

export type SegmentedSize = "sm" | "md";

export type SegmentedProps<T extends string | number> = {
  /** Names the group. Always required — an unlabelled radiogroup is noise. */
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: SegmentedSize;
  /*
   * There was an `as?: "radiogroup" | "tablist"` here, and the tablist branch
   * had no consumers. It could not have worked if one had appeared: it emitted
   * `role="tab"` with no `aria-controls`, and nothing in the app renders a
   * `role="tabpanel"` for it to point at — so the first caller to reach for it
   * would have shipped a broken widget believing the component supported one.
   * Every option in this app is a *setting*, so `radiogroup` is the only role.
   * Add the branch back with `aria-controls` in the props when a real tab strip
   * exists to need it.
   */
  /** Tabular digits, for options that are numbers or durations. */
  numeric?: boolean;
  /** Stretches the control to the container. Segments are equal either way. */
  fullWidth?: boolean;
  className?: string;
};

/** Track heights. The pill is inset 2px inside each. */
const SIZES: Record<SegmentedSize, string> = {
  sm: "h-8 text-label",
  md: "h-10 text-label",
};

/**
 * The recessed track the options sit in. Exported so an action bar can match
 * it — `DayNavigator` is the same object holding three *actions*.
 *
 * `bg-field` and no border: the track is a well, and a well is a fill. The
 * hairline box it used to carry is the outlined-control tell `IOS-DESIGN.md` §5
 * exists to remove.
 */
export function segmentedShell(className?: string): string {
  return cx("busee-seg relative inline-flex items-center rounded-control border-2 border-ps1-orange bg-ps1-panel p-1", className);
}

/**
 * One option's classes, for a tray that is not a selection — `DayNavigator`'s
 * ‹ · day · ›. `selected` still paints the pill inline here, because a tray of
 * actions has no single moving marker to position.
 */
export function segmentedItem({
  selected = false,
  size = "md",
  className,
}: {
  selected?: boolean;
  size?: SegmentedSize;
  className?: string;
} = {}): string {
  return cx(
    "inline-flex shrink-0 items-center justify-center rounded-control",
    // Deliberately no transition: see the component note.
    "transition-none select-none",
    // A minimum rather than a height, so it cannot collide with SIZES.
    "pointer-coarse:min-h-tap",
    SIZES[size],
    selected ? "bg-ps1-gold-fill text-ps1-gold-ink" : "text-muted hover:bg-hover hover:text-ink",
    "disabled:pointer-events-none disabled:text-faint",
    className,
  );
}

export function Segmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
  size = "md",
  numeric = false,
  fullWidth = false,
  className,
}: SegmentedProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // Arrow keys move the cursor and select in the same step, skipping anything
  // disabled and wrapping at both ends.
  function move(from: number, step: number) {
    const total = options.length;
    for (let hop = 1; hop <= total; hop += 1) {
      const next = (from + step * hop + total * hop) % total;
      const option = options[next];
      if (option && !option.disabled) {
        onChange(option.value);
        refs.current[next]?.focus();
        return;
      }
    }
  }

  const selectedIndex = options.findIndex((option) => option.value === value);
  const count = Math.max(options.length, 1);

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={segmentedShell(cx(fullWidth && "w-full", className))}
      style={{
        // Equal columns, so the pill below is pure arithmetic. `minmax(0,1fr)`
        // rather than `1fr` so a long label truncates instead of forcing the
        // track wider than its container. Declared inline rather than as a
        // class because it depends on `options.length`; it overrides the
        // shell's own `inline-flex`, which is what `DayNavigator` needs.
        display: fullWidth ? "grid" : "inline-grid",
        gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
      }}
    >
      {/* The pill. A positioned element rather than a background on the
          selected button: it keeps the 4px inset and the 10px radius identical
          whatever the option's own padding is, and it is one object rather than
          N states of one. `--pill` is the column width; the offset is a whole
          number of columns, so no measurement is involved. */}
      {selectedIndex >= 0 ? (
        <span
          aria-hidden
          className="pointer-events-none absolute top-1 bottom-1 left-1 rounded-control bg-ps1-gold-fill busee-segment-thumb"
          style={{
            width: `calc((100% - 0.5rem) / ${count})`,
            transform: `translateX(calc(${selectedIndex} * 100%))`,
          }}
        />
      ) : null}

      {options.map((option, index) => {
        const selected = option.value === value;
        // The divider belongs to the boundary *before* this segment and only
        // exists when neither side of that boundary is the pill.
        const divider = index > 0 && !selected && options[index - 1]?.value !== value;
        return (
          <Button
            key={String(option.value)}
            ref={(node) => {
              refs.current[index] = node;
            }}
            variant="tonal-ghost"
            size="fit"
            role="radio"
            aria-checked={selected}
            aria-label={option.ariaLabel}
            title={option.title}
            disabled={option.disabled}
            // One tab stop for the whole group. When nothing matches (a value
            // outside the options) the first option holds the stop so the
            // control is never unreachable from the keyboard.
            tabIndex={selected || (selectedIndex === -1 && index === 0) ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                event.preventDefault();
                move(index, 1);
              } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                event.preventDefault();
                move(index, -1);
              } else if (event.key === "Home") {
                event.preventDefault();
                move(-1, 1);
              } else if (event.key === "End") {
                event.preventDefault();
                move(options.length, -1);
              }
            }}
            className={cx(
              "relative z-10 inline-flex min-w-0 items-center justify-center gap-1",
              "rounded-control px-3 transition-none select-none",
              "pointer-coarse:min-h-tap",
              SIZES[size],
              selected ? "text-ps1-gold-ink" : "text-muted hover:bg-hover hover:text-ink",
              "disabled:pointer-events-none disabled:text-faint",
              // The iOS divider: a hairline between two unselected neighbours,
              // absent next to the pill. `::before` is free here — the pill is
              // a real element, so nothing else on this button uses one.
              divider &&
                "before:absolute before:inset-y-1 before:left-0 before:w-px before:bg-separator before:content-['']",
              numeric && "numeric",
            )}
          >
            {/* The label truncates, not the button: `text-overflow` does not
                apply to the anonymous flex item a bare text node becomes, so a
                long option would clip with a hard edge instead of an ellipsis. */}
            <span className="min-w-0 truncate">{option.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
